import { useEffect, useMemo, useState } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { fetchVoiceOptions, synthesizeVoiceover, TtsApiError } from '@/lib/api/ttsClient';
import { validateVoiceoverTiming } from '@/lib/validation';
import { SectionCard } from '@/components/SectionCard';
import { Select } from '@/components/Select';
import { Slider } from '@/components/Slider';
import { Button } from '@/components/Button';
import { ProgressBar } from '@/components/ProgressBar';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { getMedia } from '@/lib/db';
import { defaultVoiceoverSettings } from '@/lib/defaults';
import type { ReelProject, TtsVoiceOption, VoiceoverSettings } from '@/types';

interface VoiceoverPanelProps {
  project: ReelProject;
}

const FALLBACK_LANGUAGES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'hi-IN', label: 'Hindi' },
];

const OPENAI_VOICE_OPTIONS = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'].map((v) => ({
  value: v,
  label: v[0].toUpperCase() + v.slice(1),
}));

const OPENAI_MODEL_OPTIONS = [
  { value: 'tts-1', label: 'TTS-1 (standard, faster)' },
  { value: 'tts-1-hd', label: 'TTS-1 HD (higher quality)' },
];

function maxScriptChars(provider: VoiceoverSettings['provider']): number {
  return provider === 'openai' ? 4096 : 5000;
}

export function VoiceoverPanel({ project }: VoiceoverPanelProps) {
  const updateVoiceover = useWorkspaceStore((s) => s.updateVoiceover);
  const shared = useWorkspaceStore((s) => s.workspace.shared);
  const v = project.voiceover.overridesShared ? project.voiceover : { ...shared.voiceover, script: project.voiceover.script };

  const [voices, setVoices] = useState<TtsVoiceOption[]>([]);
  const [voicesError, setVoicesError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const generatedUrl = useObjectUrl(v.generatedMediaId);
  const timingCheck = useMemo(() => validateVoiceoverTiming(v), [v]);

  useEffect(() => {
    let cancelled = false;
    fetchVoiceOptions()
      .then((list) => !cancelled && setVoices(list))
      .catch((err) => !cancelled && setVoicesError(err instanceof TtsApiError ? err.message : 'Could not load voices.'));
    return () => {
      cancelled = true;
    };
  }, []);

  const languageOptions = useMemo(() => {
    const unique = Array.from(new Set(voices.map((voice) => voice.languageCode)));
    if (unique.length === 0) return FALLBACK_LANGUAGES;
    return unique.map((code) => ({ value: code, label: code }));
  }, [voices]);

  const voiceOptions = useMemo(() => {
    const matching = voices.filter((voice) => voice.languageCode === v.languageCode);
    if (matching.length === 0) {
      return [{ value: v.voiceName, label: v.voiceName }];
    }
    return matching.map((voice) => ({ value: voice.name, label: `${voice.name} (${voice.ssmlGender.toLowerCase()})` }));
  }, [voices, v.languageCode, v.voiceName]);

  function patch(fields: Partial<VoiceoverSettings>) {
    updateVoiceover(project.id, { ...fields, overridesShared: true });
  }

  async function handlePreview() {
    if (!v.script.trim()) {
      setGenError('Enter a script before previewing a voice sample.');
      return;
    }
    setGenError(null);
    setIsPreviewing(true);
    try {
      const sample = v.script.trim().slice(0, 200);
      const result = await synthesizeVoiceover(
        v.provider === 'google'
          ? { provider: 'google', text: sample, languageCode: v.languageCode, voiceName: v.voiceName, voiceGender: v.voiceGender, speakingRate: v.speakingRate, pitch: v.pitch }
          : { provider: 'openai', text: sample, openaiVoice: v.openaiVoice, openaiModel: v.openaiModel, speakingRate: v.speakingRate }
      );
      const record = await getMedia(result.asset.id);
      if (record) {
        const blobUrl = URL.createObjectURL(record.blob);
        setPreviewUrl(blobUrl);
        new Audio(blobUrl).play().catch(() => undefined);
      }
    } catch (err) {
      setGenError(err instanceof TtsApiError ? err.message : 'Could not generate a preview sample.');
    } finally {
      setIsPreviewing(false);
    }
  }

  async function handleGenerate() {
    if (!v.script.trim()) {
      setGenError('Enter a voiceover script first.');
      return;
    }
    setGenError(null);
    setIsGenerating(true);
    try {
      const result = await synthesizeVoiceover(
        v.provider === 'google'
          ? { provider: 'google', text: v.script, languageCode: v.languageCode, voiceName: v.voiceName, voiceGender: v.voiceGender, speakingRate: v.speakingRate, pitch: v.pitch }
          : { provider: 'openai', text: v.script, openaiVoice: v.openaiVoice, openaiModel: v.openaiModel, speakingRate: v.speakingRate }
      );
      patch({
        generatedMediaId: result.asset.id,
        generatedDurationSeconds: result.durationSeconds,
        wordTimings: result.wordTimings,
        timingSource: result.timingSource,
        trimStart: 0,
        trimEnd: 0,
      });
    } catch (err) {
      setGenError(err instanceof TtsApiError ? err.message : 'Voiceover generation failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2 text-xs dark:bg-slate-800">
        <span className="text-slate-500 dark:text-slate-400">Provider, voice, rate & volume come from the shared workspace defaults unless overridden here.</span>
        <label className="flex shrink-0 items-center gap-2">
          <input
            type="checkbox"
            checked={project.voiceover.overridesShared}
            onChange={(e) => updateVoiceover(project.id, { overridesShared: e.target.checked })}
          />
          Override for this Reel
        </label>
      </div>

      <SectionCard title="Script" onReset={() => patch({ script: '' })}>
        <textarea
          value={v.script}
          onChange={(e) => patch({ script: e.target.value })}
          rows={6}
          placeholder="Paste or write the voiceover script for this Reel…"
          className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        />
        <p className="mt-1 text-right text-xs text-slate-400">
          {v.script.length} / {maxScriptChars(v.provider)} characters
        </p>
      </SectionCard>

      <SectionCard
        title="Voice"
        onReset={() => patch({ ...defaultVoiceoverSettings(), script: v.script })}
      >
        <Select
          label="Voiceover provider"
          value={v.provider}
          options={[
            { value: 'google', label: 'Google Cloud Text-to-Speech' },
            { value: 'openai', label: 'OpenAI TTS-1' },
          ]}
          onChange={(val) => patch({ provider: val as VoiceoverSettings['provider'] })}
          tooltip="Google offers per-language voice selection; OpenAI TTS-1 offers 6 fixed voices that auto-detect language, with no pitch control."
        />

        {v.provider === 'google' ? (
          <>
            {voicesError && <p className="text-xs text-amber-600">{voicesError} Using a standard voice list instead.</p>}
            <div className="grid grid-cols-2 gap-2">
              <Select label="Language" value={v.languageCode} options={languageOptions} onChange={(val) => patch({ languageCode: val })} />
              <Select label="Voice" value={v.voiceName} options={voiceOptions} onChange={(val) => patch({ voiceName: val })} />
            </div>
            <Select
              label="Gender"
              value={v.voiceGender}
              options={[
                { value: 'FEMALE', label: 'Female' },
                { value: 'MALE', label: 'Male' },
                { value: 'NEUTRAL', label: 'Neutral' },
              ]}
              onChange={(val) => patch({ voiceGender: val as VoiceoverSettings['voiceGender'] })}
            />
            <div className="grid grid-cols-3 gap-2">
              <Slider label="Speed" value={v.speakingRate} min={0.5} max={2} step={0.05} formatValue={(x) => `${x.toFixed(2)}×`} onChange={(val) => patch({ speakingRate: val })} />
              <Slider label="Pitch" value={v.pitch} min={-10} max={10} step={0.5} onChange={(val) => patch({ pitch: val })} />
              <Slider label="Volume" value={v.volume} min={0} max={1.5} step={0.05} formatValue={(x) => `${Math.round(x * 100)}%`} onChange={(val) => patch({ volume: val })} />
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Select label="Voice" value={v.openaiVoice} options={OPENAI_VOICE_OPTIONS} onChange={(val) => patch({ openaiVoice: val as VoiceoverSettings['openaiVoice'] })} />
              <Select label="Model" value={v.openaiModel} options={OPENAI_MODEL_OPTIONS} onChange={(val) => patch({ openaiModel: val as VoiceoverSettings['openaiModel'] })} />
            </div>
            <p className="text-xs text-slate-400">Language is auto-detected from your script. OpenAI TTS-1 doesn't support pitch adjustment.</p>
            <div className="grid grid-cols-2 gap-2">
              <Slider label="Speed" value={v.speakingRate} min={0.25} max={4} step={0.05} formatValue={(x) => `${x.toFixed(2)}×`} onChange={(val) => patch({ speakingRate: val })} />
              <Slider label="Volume" value={v.volume} min={0} max={1.5} step={0.05} formatValue={(x) => `${Math.round(x * 100)}%`} onChange={(val) => patch({ volume: val })} />
            </div>
          </>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="secondary" onClick={handlePreview} disabled={isPreviewing}>
            {isPreviewing ? 'Generating sample…' : 'Preview sample'}
          </Button>
          <Button variant="primary" onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? 'Generating…' : v.generatedMediaId ? 'Regenerate voiceover' : 'Generate voiceover'}
          </Button>
        </div>
        {genError && <p className="text-xs text-red-600">{genError}</p>}
        {previewUrl && <audio controls src={previewUrl} className="mt-1 w-full" />}

        {v.generatedMediaId && (
          <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-800/60">
            <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">
              Duration: {v.generatedDurationSeconds?.toFixed(2)}s · Timing: {v.timingSource === 'marks' ? 'precise (provider timepoints)' : 'estimated'}
            </p>
            {generatedUrl && <audio controls src={generatedUrl} className="w-full" />}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Timeline placement">
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="Start at (s)" value={v.startAt} onChange={(val) => patch({ startAt: val === '' ? 0 : val })} min={0} />
          <NumberField label="End at (s, optional)" value={v.endAt ?? ''} onChange={(val) => patch({ endAt: val === '' ? null : Number(val) })} min={0} allowEmpty />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Slider label="Silence before" value={v.silenceBefore} min={0} max={3} step={0.1} formatValue={(x) => `${x.toFixed(1)}s`} onChange={(val) => patch({ silenceBefore: val })} />
          <Slider label="Silence after" value={v.silenceAfter} min={0} max={3} step={0.1} formatValue={(x) => `${x.toFixed(1)}s`} onChange={(val) => patch({ silenceAfter: val })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Slider
            label="Trim start"
            value={v.trimStart}
            min={0}
            max={Math.max(0.1, (v.generatedDurationSeconds ?? 10) - 0.1)}
            step={0.1}
            formatValue={(x) => `${x.toFixed(1)}s`}
            onChange={(val) => patch({ trimStart: val })}
          />
          <Slider
            label="Trim end"
            value={v.trimEnd}
            min={0}
            max={Math.max(0.1, (v.generatedDurationSeconds ?? 10) - 0.1)}
            step={0.1}
            formatValue={(x) => `${x.toFixed(1)}s`}
            onChange={(val) => patch({ trimEnd: val })}
          />
        </div>
        {!timingCheck.valid && (
          <ul className="list-inside list-disc text-xs text-red-600">
            {timingCheck.errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        )}
      </SectionCard>

      {isGenerating && <ProgressBar value={50} label="Generating voiceover" />}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  allowEmpty,
}: {
  label: string;
  value: number | '';
  onChange: (value: number | '') => void;
  min?: number;
  allowEmpty?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        step={0.1}
        onChange={(e) => onChange(allowEmpty && e.target.value === '' ? '' : Number(e.target.value))}
        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-brand-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      />
    </label>
  );
}
